/**
 * The damage taxonomy (CR §1.9, rules inventory §10.8) and the pipeline every damage event
 * runs through.
 *
 * Deal, put, remove and move are four different pipelines wearing one word, and the engine
 * has to keep them apart because cards do:
 *
 * | Verb | Damage modifiers (Resist) | Emits | "Dealt damage" triggers |
 * |---|---|---|---|
 * | `deal` | **yes** (CR §8.9) | `damage-dealt` (`verb: 'deal'`) | fire |
 * | `put` | no | `damage-dealt` (`verb: 'put'`) | do not fire — it is not *dealt* |
 * | `remove` | n/a | `damage-removed` | — |
 * | `move` | **no** — moving is neither dealing nor putting | `damage-removed` + `damage-moved` | do not fire |
 *
 * Every one of them is a **pending event** first (`replacements.ts`): nothing is written to
 * the state and nothing is emitted until the replacement pipeline has had its say. The order
 * inside one event is fixed by the CR 2.2 clarification —
 *
 * ```
 *   damage modifiers (Resist)  →  replacement effects  →  it happens
 * ```
 *
 * — which is why the Resist reduction lives here, at the top of the pipeline, rather than
 * where the counters are finally written: a replacement catching "would be dealt 3 damage"
 * against Resist +1 sees 2, and that is the number it carries across.
 *
 * A replaced event is **never performed**, so nothing is emitted for it; since `GameEvent` is
 * what the bag matches triggers against, its would-have-been triggers never fire. The events
 * a replacement produces re-enter this pipeline (so replacements chain) and are performed
 * depth-first, each one fully resolved — including its own replacements — before the next.
 */
import type { InstanceId, PlayerIndex } from './ids.ts';
import { resistOf } from './abilities/keywords.ts';
import { canGainLore } from './modifiers.ts';
import type {
  DamagePendingEvent,
  DiscardPendingEvent,
  InkPendingEvent,
  PendingEvent,
  ReplacementMatch,
  ToInkwellPendingEvent,
} from './replacements.ts';
import {
  consumeIfLimited,
  consumeIfOneShot,
  hasAmount,
  nextReplacement,
  rewriteEvent,
} from './replacements.ts';
import type { GameState } from './state.ts';
import { instanceAt } from './state.ts';
import {
  announceInked,
  banish,
  challengeContext,
  emit,
  gainLore,
  moveInstance,
  putIntoInkwell,
} from './zones.ts';

/**
 * A replacement that produces an event another replacement catches is legal and expected;
 * a cycle of them is not, and the once-per-event rule (CR §6.5.6) already makes one
 * impossible. This is the tripwire in case a future event kind finds a way around it.
 */
const MAX_REPLACEMENT_DEPTH = 64;

// ---------------------------------------------------------------------------
// The four verbs
// ---------------------------------------------------------------------------

/**
 * Deal or put damage on a card in play.
 *
 * `deal` is challenge damage and "deal N damage" effects — **Resist +N reduces it**
 * (CR §8.9), never below zero. `put` is "put N damage counters on", which bypasses damage
 * modifiers entirely. Both then go through the replacement pipeline.
 */
export function dealDamage(
  state: GameState,
  id: InstanceId,
  amount: number,
  source: InstanceId | null,
  verb: 'deal' | 'put' = 'deal',
  options: { ignoresResist?: boolean } = {},
): void {
  runReplaceableEvent(
    state,
    {
      kind: 'damage',
      verb,
      target: id,
      from: null,
      amount,
      source,
      ...(options.ignoresResist === undefined ? {} : { ignoresResist: options.ignoresResist }),
    },
    [],
    0,
  );
}

/** "Put N damage counters on …" — `dealDamage`'s `put` verb, named (CR §1.9). */
export function putDamage(
  state: GameState,
  id: InstanceId,
  amount: number,
  source: InstanceId | null,
): void {
  dealDamage(state, id, amount, source, 'put');
}

/** Remove damage counters from a card (CR §1.9's `remove` verb). Clamped at zero. */
export function removeDamage(state: GameState, id: InstanceId, amount: number): void {
  runReplaceableEvent(
    state,
    { kind: 'damage', verb: 'remove', target: id, from: null, amount, source: null },
    [],
    0,
  );
}

/**
 * Move damage counters from one card to another (CR §1.9).
 *
 * Remove plus put in one event, and **neither** for rules purposes: no damage modifier
 * applies and nothing that triggers on damage being dealt or put sees it. It does emit
 * `damage-removed` for the donor, because moving counts as removing for triggers (official
 * *Wilds Unknown* Q&A). Damage never moves back onto the card it came from.
 */
export function moveDamage(
  state: GameState,
  from: InstanceId,
  to: InstanceId,
  amount: number,
  source: InstanceId | null = null,
): void {
  if (from === to) return;
  runReplaceableEvent(
    state,
    { kind: 'damage', verb: 'move', target: to, from, amount, source },
    [],
    0,
  );
}

/**
 * Two `deal` damage events that happen **simultaneously** (CR §4.6.5) — challenge damage's
 * shape, where the challenger and the defender each deal their {S} to the other at once.
 *
 * `runReplaceableEvent` normally decides-and-performs one event in a single pass, which is
 * exactly wrong for "simultaneously": deciding (damage modifiers, then which replacement
 * matches) has to happen for *both* sides against the same one pre-challenge board, before
 * either side's damage is actually written — otherwise a replacement guard evaluated for the
 * second side (Raya - Leader of Heart's "takes no damage from a damaged character" among them)
 * would see the first side's damage already landed. So this splits the pipeline into
 * `prepareReplaceableEvent` (modifiers + replacement lookup, no mutation) and
 * `commitReplaceableEvent` (consume + perform, or recurse into what a replacement produced),
 * runs *prepare* for both events back to back — neither one writes anything, so order between
 * them can't matter — and only then runs *commit* for both.
 */
export function dealSimultaneousDamage(
  state: GameState,
  a: { readonly target: InstanceId; readonly amount: number; readonly source: InstanceId | null },
  b: { readonly target: InstanceId; readonly amount: number; readonly source: InstanceId | null },
): void {
  const eventA: DamagePendingEvent = {
    kind: 'damage',
    verb: 'deal',
    target: a.target,
    from: null,
    amount: a.amount,
    source: a.source,
  };
  const eventB: DamagePendingEvent = {
    kind: 'damage',
    verb: 'deal',
    target: b.target,
    from: null,
    amount: b.amount,
    source: b.source,
  };
  const preparedA = prepareReplaceableEvent(state, eventA, []);
  const preparedB = prepareReplaceableEvent(state, eventB, []);
  commitReplaceableEvent(state, preparedA, 0);
  commitReplaceableEvent(state, preparedB, 0);
}

// ---------------------------------------------------------------------------
// The pipeline
// ---------------------------------------------------------------------------

/**
 * Modifiers, then replacements, then the event happens — for one pending event and everything
 * it turns into. `applied` is the set of replacements already spent on this event's lineage
 * (CR §6.5.6), which the recursion carries down each branch.
 *
 * The runner is one function for every replaceable event kind, because the pipeline is the
 * same regardless of what is pending; only the modifier pass (damage-only, Resist) and the
 * final perform differ by kind. A produced event is almost always damage (`rewriteEvent`); the
 * one exception is `ink`, whose own replacements only ever produce another `ink` event, and
 * the once-per-event dedup (CR §6.5.6) is what keeps that from looping.
 *
 * Just `prepareReplaceableEvent` then `commitReplaceableEvent` back to back — the ordinary,
 * one-event-at-a-time path. `dealSimultaneousDamage` is what needs them split.
 */
function runReplaceableEvent(
  state: GameState,
  event: PendingEvent,
  applied: readonly string[],
  depth: number,
): void {
  if (depth > MAX_REPLACEMENT_DEPTH) {
    throw new Error('Replacement effects did not settle — an event is replacing itself');
  }
  commitReplaceableEvent(state, prepareReplaceableEvent(state, event, applied), depth);
}

/** What would happen to one pending event, decided but not yet done — see `runReplaceableEvent`. */
interface PreparedEvent {
  readonly event: PendingEvent;
  readonly match: ReplacementMatch | null;
  readonly applied: readonly string[];
}

/**
 * Damage modifiers, then the replacement lookup — the "decide" half of the pipeline. Reads
 * only; nothing here writes a damage counter or emits anything, which is what lets
 * `dealSimultaneousDamage` call this for two events back to back against one shared,
 * unmutated board before either is committed.
 */
function prepareReplaceableEvent(
  state: GameState,
  event: PendingEvent,
  applied: readonly string[],
): PreparedEvent {
  const modified = event.kind === 'damage' ? applyDamageModifiers(state, event) : event;
  // Nothing to do is not an event: it can neither be replaced nor trigger anything. `ink`,
  // `banish` and `to-inkwell` have no natural "amount" — each is always exactly one card — so
  // they skip this check (`hasAmount`).
  if (hasAmount(modified) && modified.amount <= 0) {
    return { event: modified, match: null, applied };
  }
  return { event: modified, match: nextReplacement(state, modified, applied), applied };
}

/** Perform a decided event, or consume its match and recurse into what it became — the "act" half. */
function commitReplaceableEvent(state: GameState, prepared: PreparedEvent, depth: number): void {
  const { event: modified, match, applied } = prepared;
  if (hasAmount(modified) && modified.amount <= 0) return;
  if (match === null) {
    performEvent(state, modified);
    return;
  }
  // The replaced event never happens; only what it became does. A `consumesOnApply`
  // replacement (and a `limit: 'once-per-turn'` one) is crossed off right here — it applied,
  // not merely matched.
  consumeIfOneShot(match);
  consumeIfLimited(state, match);
  const spent = [...applied, match.key, match.dedupe];
  for (const produced of rewriteEvent(state, modified, match)) {
    runReplaceableEvent(state, produced, spent, depth + 1);
  }
}

/** The pending event actually happening, by kind. */
function performEvent(state: GameState, event: PendingEvent): void {
  switch (event.kind) {
    case 'damage':
      performDamage(state, event);
      return;
    case 'lose-lore':
      // `gainLore` clamps at zero and emits `lore-changed` — a prevented loss emits nothing.
      gainLore(state, event.player, -event.amount, event.source);
      return;
    case 'gain-lore':
      gainLore(state, event.player, event.amount, event.source);
      return;
    case 'ink':
      performInk(state, event);
      return;
    case 'banish':
      // Unreplaced, this *is* the banish — `zones.ts`'s own chokepoint, snapshots and all
      // (tickets 68, 104). A replaced banish never reaches here at all.
      banish(state, event.target, event.source);
      return;
    case 'to-inkwell':
      performToInkwell(state, event);
      return;
    case 'discard':
      // Never reached: a discard enters through `discardPrevented`, which leaves the
      // performing — a choice, or the RNG's pick — to the interpreter, and a discard's own
      // `rewriteEvent` can only ever produce damage.
      throw new Error('A discard event cannot be performed by the pipeline');
  }
}

/** The event actually happening: the card lands in the inkwell, exerted as the event says. */
function performInk(state: GameState, event: InkPendingEvent): void {
  moveInstance(state, event.card, 'inkwell');
  instanceAt(state, event.card).exerted = event.exerted;
  announceInked(state, event.player, event.card, event.defId);
}

/**
 * The event actually happening: a `banish` replacement's own redirect lands the card in an
 * inkwell — `zones.ts`'s `putIntoInkwell`, the same primitive an effect-driven put uses, so it
 * gets the same facedown-knowledge and `card-inked` handling without a bespoke case here.
 */
function performToInkwell(state: GameState, event: ToInkwellPendingEvent): void {
  putIntoInkwell(state, event.card, event.player, {
    exerted: event.exerted,
    facedown: event.facedown,
  });
}

/**
 * An effect would make `player` lose `amount` lore (CR §6.6.3), through the replacement
 * pipeline: a replacement that catches it prevents (or rewrites) the loss, and a prevented
 * loss emits no `lore-changed` at all.
 */
export function loseLore(
  state: GameState,
  player: PlayerIndex,
  amount: number,
  source: InstanceId | null,
): void {
  runReplaceableEvent(state, { kind: 'lose-lore', player, amount, source }, [], 0);
}

/**
 * `player` **gains** `amount` lore — the one chokepoint every gain passes through: questing
 * (`rules.ts`), a location's ◊ in the Set step, and an effect's "gain N lore"
 * (`interpret.ts`). `zones.ts`'s `gainLore` is the raw counter mutation underneath; this is
 * the rules-level event, and only what comes through here can be stopped or replaced.
 *
 * Two things get a look, in this order:
 *
 * 1. **"Can't gain lore"** (CR §6.6.5) — a continuous, player-scoped restriction read live by
 *    `canGainLore`. A gain that can't happen is not an event at all: it can neither be
 *    replaced nor emit anything, exactly as a zero-amount one cannot.
 * 2. **Replacement effects** (CR §6.5) — the ordinary pipeline, for the "…gains that much lore
 *    instead" family.
 */
export function gainLoreEvent(
  state: GameState,
  player: PlayerIndex,
  amount: number,
  source: InstanceId | null = null,
): void {
  if (amount <= 0) return;
  if (!canGainLore(state, player)) return;
  runReplaceableEvent(state, { kind: 'gain-lore', player, amount, source }, [], 0);
}

/**
 * `card` enters `player`'s inkwell (CR §4.2) — the chokepoint the ordinary once-per-turn ink
 * action (`rules.ts`) runs through. It arrives ready by default; a replacement — "cards enter
 * opponents' inkwells exerted" (Daisy Duck - Paranormal Investigator) — can flip that to
 * exerted instead. Effect-driven puts (`putIntoInkwell`, `zones.ts`) do not go through here and
 * set their own exertion directly.
 */
export function inkEvent(
  state: GameState,
  player: PlayerIndex,
  card: InstanceId,
  defId: number,
  source: InstanceId | null = null,
): void {
  runReplaceableEvent(state, { kind: 'ink', player, card, defId, exerted: false, source }, [], 0);
}

/**
 * `card` **would be banished** (CR §6.5, ticket 137) — the chokepoint every banish now runs
 * through: a challenge, an effect, a cost payment (`banish-self`/`banish-cards`), and the
 * lethal-damage game state check alike, exactly as `gainLoreEvent` unified every source of a
 * lore gain. A replacement that catches it (Mickey Mouse & Minnie Mouse - Adventuring Duo's
 * "put them into your inkwell facedown and exerted instead") redirects the card and the banish
 * never happens at all — no `card-banished`, no pre-banish snapshot, nothing for a "when this
 * is banished" trigger to see. Unreplaced, `zones.ts`'s `banish` performs exactly as before.
 */
export function banishEvent(
  state: GameState,
  id: InstanceId,
  source: InstanceId | null = null,
): void {
  runReplaceableEvent(state, { kind: 'banish', target: id, source }, [], 0);
}

/**
 * Offer a would-be discard of `amount` cards from `player`'s hand to the replacement
 * pipeline, **before** anyone is asked which cards. Returns `true` when a replacement caught
 * it — whatever it produced instead has already run, and the caller must not discard.
 * Returns `false` when the discard survives and the caller performs it as usual.
 *
 * The event is raised whole rather than per card because the cards are not chosen yet —
 * "you don't discard" cancels the requirement, not a selection.
 */
export function discardPrevented(
  state: GameState,
  player: PlayerIndex,
  amount: number,
  source: InstanceId | null,
): boolean {
  if (amount <= 0) return false;
  const event: DiscardPendingEvent = { kind: 'discard', player, amount, source };
  const match = nextReplacement(state, event, []);
  if (match === null) return false;
  consumeIfOneShot(match);
  consumeIfLimited(state, match);
  const spent = [match.key, match.dedupe];
  for (const produced of rewriteEvent(state, event, match)) {
    runReplaceableEvent(state, produced, spent, 1);
  }
  return true;
}

/**
 * Apply damage modifiers, which the CR 2.2 clarification puts **before** replacement effects.
 * Resist +N is the only one today, and it reduces *dealt* damage only (CR §8.9) — never put,
 * never moved.
 */
function applyDamageModifiers(state: GameState, event: DamagePendingEvent): DamagePendingEvent {
  if (event.verb !== 'deal' || event.ignoresResist === true) return event;
  const reduced = Math.max(0, event.amount - resistOf(state, instanceAt(state, event.target)));
  return reduced === event.amount ? event : { ...event, amount: reduced };
}

/** The event actually happening: counters move, and only now is anything emitted. */
function performDamage(state: GameState, event: DamagePendingEvent): void {
  const target = instanceAt(state, event.target);
  // "Do as much as you can" (CR §1.2.3): damage on a card that has left play means nothing.
  if (target.zone !== 'play') return;

  switch (event.verb) {
    case 'deal':
    case 'put':
      target.damage += event.amount;
      // The per-turn fact "a character took damage this turn" (deal or put, CR §1.9's umbrella
      // reading) is written exactly where `damage-dealt` fires for these two verbs, so the two
      // can never disagree; the Ready step clears it. `move` does not reach this case — it is
      // neither dealing nor putting — so it does not count, matching the `damageVerb`-absent
      // trigger reading (`ir.ts`).
      state.damagedThisTurn.push(target.id);
      emit(state, {
        type: 'damage-dealt',
        instance: target.id,
        amount: event.amount,
        source: event.source,
        total: target.damage,
        verb: event.verb,
        challengeRole: challengeContext(state, target.id)?.role ?? null,
      });
      return;
    case 'remove': {
      const removed = Math.min(target.damage, event.amount);
      if (removed === 0) return;
      target.damage -= removed;
      if (state.catalog.get(target.defId)?.type === 'Character') {
        state.damageRemovedThisTurn[target.controller] =
          (state.damageRemovedThisTurn[target.controller] ?? 0) + removed;
      }
      emit(state, {
        type: 'damage-removed',
        instance: target.id,
        amount: removed,
        total: target.damage,
      });
      return;
    }
    case 'move': {
      if (event.from === null || event.from === target.id) return;
      const donor = instanceAt(state, event.from);
      if (donor.zone !== 'play') return;
      const moved = Math.min(donor.damage, event.amount);
      if (moved === 0) return;
      donor.damage -= moved;
      target.damage += moved;
      if (state.catalog.get(donor.defId)?.type === 'Character') {
        state.damageRemovedThisTurn[donor.controller] =
          (state.damageRemovedThisTurn[donor.controller] ?? 0) + moved;
      }
      // Moving counts as *removing* for triggers, and as neither dealing nor putting.
      emit(state, {
        type: 'damage-removed',
        instance: donor.id,
        amount: moved,
        total: donor.damage,
      });
      emit(state, {
        type: 'damage-moved',
        instance: target.id,
        from: donor.id,
        amount: moved,
        total: target.damage,
      });
      return;
    }
  }
}
